/**
 * Public accept-invite page. Lands here when a teammate clicks the
 * link in the Resend email. We preview *who* invited them + *which
 * role* without requiring auth, then walk them through sign-in /
 * sign-up before calling /api/landlord/team/accept/{token}.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import PublicHeader from "@/components/layout/PublicHeader";
import { Card, Button, Loader } from "@/components/ui/Primitives";
import { Users, CheckCircle2, ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function AcceptInvite() {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/landlord/team/invite/${token}`)
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d.detail || "Invite not found")))
      .then(setPreview)
      .catch(e => setError(String(e)));
  }, [token]);

  async function accept() {
    if (!user) {
      // Bounce through the landlord login screen with the next= param
      // preserved so we come right back here post-auth (the bare /login
      // route silently forwards to /login/landlord and drops the
      // query string, so go directly).
      const next = encodeURIComponent(`/team/accept/${token}`);
      navigate(`/login/landlord?next=${next}`);
      return;
    }
    setBusy(true);
    try {
      const uid = user.id || user.user_id || user.email;
      const r = await fetch(`${API}/api/landlord/team/accept/${token}`, {
        method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": uid },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Could not accept");
      setAccepted(true);
      toast.success("You're on the team!");
      setTimeout(() => navigate("/team"), 1800);
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader/>
      <main className="max-w-md mx-auto px-6 py-16">
        <Card className="p-8 text-center" data-testid="accept-invite-card">
          {!preview && !error && <Loader/>}
          {error && (
            <div>
              <div className="font-display text-xl font-extrabold text-slate-900 mb-2">Invite not available</div>
              <p className="text-sm text-slate-600 mb-5">This invite may have already been accepted or expired. Ask the team owner to resend.</p>
              <Link to="/" className="text-sm font-bold text-emerald-700 hover:underline">← Back to home</Link>
            </div>
          )}
          {preview && !accepted && (
            <div>
              <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-4"><Users className="w-7 h-7"/></div>
              <div className="text-xs uppercase tracking-widest font-bold text-emerald-700 mb-1">You&apos;re invited</div>
              <h1 className="font-display text-2xl font-extrabold text-slate-900 mb-2" data-testid="accept-invite-headline">Join {preview.invited_by_name}&apos;s team</h1>
              <p className="text-sm text-slate-600 mb-5">on Central Rentals as a <b className="text-slate-900">{preview.role_label}</b>.</p>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 mb-5 inline-flex items-center gap-2">
                <Mail className="w-3.5 h-3.5"/> Invite sent to <span className="font-bold text-slate-900">{preview.email}</span>
              </div>
              <Button onClick={accept} disabled={busy} className="w-full" data-testid="accept-invite-btn">
                {busy ? "Accepting…" : user ? <>Accept &amp; continue <ArrowRight className="w-4 h-4 inline ml-1"/></> : "Sign in to accept"}
              </Button>
              {!user && <p className="text-xs text-slate-500 mt-3">Don&apos;t have an account yet? <Link to={`/signup?next=${encodeURIComponent(`/team/accept/${token}`)}`} className="font-bold text-emerald-700 hover:underline">Sign up</Link> first — it takes about 30 seconds.</p>}
            </div>
          )}
          {accepted && (
            <div data-testid="accept-invite-success">
              <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-3"/>
              <div className="font-display text-xl font-extrabold text-slate-900">You&apos;re on the team</div>
              <p className="text-sm text-slate-600 mt-1">Redirecting to your portal…</p>
              <button
                type="button"
                onClick={() => navigate("/team", { replace: true })}
                data-testid="accept-invite-manual-redirect"
                className="mt-3 text-xs font-semibold text-emerald-700 underline hover:text-emerald-800"
              >
                Not redirecting? Tap here.
              </button>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
